<template>
  <div class="waterfall">
    <!-- The whole timeline scrolls horizontally inside this wrapper so it can
         never widen the page at mobile width. -->
    <div class="wf-scroll">
      <div class="wf">
        <!-- Ruler: 0 → total trace duration -->
        <div class="wf-head" aria-hidden="true">
          <span class="wf-head-label">span</span>
          <span class="wf-ruler">
            <span v-for="t in ticks" :key="t.frac" class="wf-tick" :style="{ left: t.left }">
              <span class="wf-tick-label">{{ t.label }}</span>
            </span>
          </span>
        </div>

        <template v-for="row in rows" :key="row.span.spanId">
          <button
            type="button"
            class="wf-row"
            :class="{ 'is-open': openSpanId === row.span.spanId, 'is-error': isError(row.span) }"
            :aria-expanded="openSpanId === row.span.spanId"
            :title="`${row.span.name} — ${formatDurationNs(row.span.durationNs)}`"
            @click="toggle(row.span.spanId)"
          >
            <span class="wf-label" :style="{ paddingLeft: `${row.depth * 14}px` }">
              <span class="wf-name">{{ row.span.name }}</span>
              <span class="wf-svc">{{ row.span.service }}</span>
            </span>
            <span class="wf-track">
              <span
                class="wf-bar"
                :style="{
                  left: pct(row.offsetFrac),
                  width: pct(row.widthFrac),
                  background: barColor(row.span),
                }"
              />
              <span class="wf-dur" :class="{ before: durBefore(row) }" :style="durStyle(row)">{{
                formatDurationNs(row.span.durationNs)
              }}</span>
            </span>
          </button>

          <!-- Expanded span detail (inline) -->
          <div v-if="openSpanId === row.span.spanId" class="wf-detail">
            <div class="wf-meta">
              <span class="meta-item"
                ><span class="meta-k">service</span>{{ row.span.service }}</span
              >
              <span class="meta-item"
                ><span class="meta-k">kind</span>{{ kindLabel(row.span.kind) }}</span
              >
              <span class="meta-item"
                ><span class="meta-k">duration</span
                >{{ formatDurationNs(row.span.durationNs) }}</span
              >
              <span class="meta-item"
                ><span class="meta-k">status</span>
                <span
                  :class="{
                    'status-err': isError(row.span),
                    'status-ok': row.span.statusCode === 1,
                  }"
                >
                  {{ statusLabel(row.span.statusCode) }}
                </span></span
              >
              <span v-if="row.span.httpMethod" class="meta-item"
                ><span class="meta-k">http</span>{{ row.span.httpMethod }}
                {{ row.span.httpRoute ?? '' }}
                <template v-if="row.span.httpStatus"> → {{ row.span.httpStatus }}</template></span
              >
              <span v-if="row.span.userId" class="meta-item"
                ><span class="meta-k">user</span>{{ row.span.userId }}</span
              >
            </div>

            <p v-if="row.span.statusMessage" class="wf-status-msg">{{ row.span.statusMessage }}</p>

            <ul v-if="attrEntries(row.span).length" class="wf-attrs">
              <li v-for="[k, v] in attrEntries(row.span)" :key="k" class="wf-attr">
                <span class="attr-k">{{ k }}</span
                ><span class="attr-eq" aria-hidden="true">=</span
                ><span class="attr-v">{{ displayValue(v) }}</span>
              </li>
            </ul>
            <p v-else-if="!row.span.statusMessage" class="wf-no-attrs">
              No extra details on this span.
            </p>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue';
import type { TraceSpan } from '../../api.js';
import { layoutSpans, formatDurationNs } from '../../lib/trace-layout.js';
import { resolveVizPalette, colorForName } from '../../lib/viz-palette.js';

const props = defineProps<{ spans: TraceSpan[] }>();

const rows = computed(() => layoutSpans(props.spans));

const totalNs = computed(() => {
  if (!props.spans.length) return 0;
  let start = Infinity;
  let end = -Infinity;
  for (const s of props.spans) {
    if (s.startNs < start) start = s.startNs;
    if (s.endNs > end) end = s.endNs;
  }
  return Math.max(0, end - start);
});

/** Ruler ticks at 0 / 25 / 50 / 75 / 100% of the trace. */
const ticks = computed(() =>
  [0, 0.25, 0.5, 0.75, 1].map((frac) => ({
    frac,
    left: pct(frac),
    label: frac === 0 ? '0' : formatDurationNs(totalNs.value * frac),
  })),
);

function pct(frac: number): string {
  return `${(frac * 100).toFixed(2)}%`;
}

// --- Bar colors: stable per service, red for error spans -------------------
const palette = ref<string[]>([]);
onMounted(() => {
  palette.value = resolveVizPalette(document.body);
});

function isError(s: TraceSpan): boolean {
  return s.statusCode === 2;
}
function barColor(s: TraceSpan): string {
  if (isError(s)) return 'var(--lg-danger)';
  return colorForName(s.service, palette.value.length ? palette.value : ['#58a6ff']);
}

/** Put the duration text after the bar unless the bar ends near the right
 * edge — then it sits just before the bar so it never clips. */
function durBefore(row: { offsetFrac: number; widthFrac: number }): boolean {
  return row.offsetFrac + row.widthFrac > 0.82;
}
function durStyle(row: { offsetFrac: number; widthFrac: number }): Record<string, string> {
  if (durBefore(row)) {
    return { right: pct(1 - row.offsetFrac) };
  }
  return { left: pct(Math.min(1, row.offsetFrac + row.widthFrac)) };
}

// --- Expanded span --------------------------------------------------------
const openSpanId = ref<string | null>(null);
function toggle(spanId: string) {
  openSpanId.value = openSpanId.value === spanId ? null : spanId;
}
// A new trace's spans → collapse any stale expansion.
watch(
  () => props.spans,
  () => {
    openSpanId.value = null;
  },
);

const KIND_LABELS = ['unset', 'internal', 'server', 'client', 'producer', 'consumer'];
function kindLabel(kind: number): string {
  return KIND_LABELS[kind] ?? String(kind);
}
function statusLabel(code: number): string {
  return code === 2 ? 'error' : code === 1 ? 'ok' : '—';
}

function attrEntries(s: TraceSpan): Array<[string, unknown]> {
  return s.attrs ? Object.entries(s.attrs) : [];
}
function displayValue(v: unknown): string {
  return typeof v === 'string' ? v : JSON.stringify(v);
}
</script>

<style scoped>
.waterfall {
  min-width: 0;
}
.wf-scroll {
  overflow-x: auto;
  border: 1px solid var(--lg-border-faint);
  border-radius: var(--lg-radius-md);
  background: var(--lg-bg);
}
.wf {
  min-width: 560px;
}

/* --- Header / ruler --- */
.wf-head {
  display: flex;
  align-items: flex-end;
  border-bottom: 1px solid var(--lg-border);
  color: var(--lg-text-faint);
  font-size: var(--lg-fs-2xs);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}
.wf-head-label {
  flex: 0 0 220px;
  padding: var(--lg-space-2) var(--lg-space-2) var(--lg-space-1) var(--lg-space-3);
}
.wf-ruler {
  position: relative;
  flex: 1;
  min-width: 0;
  height: 22px;
  margin-right: var(--lg-space-3);
}
.wf-tick {
  position: absolute;
  bottom: 0;
  top: 0;
  border-left: 1px solid var(--lg-border-faint);
}
.wf-tick-label {
  position: absolute;
  bottom: 2px;
  left: 3px;
  white-space: nowrap;
  font-family: var(--lg-font-mono);
  text-transform: none;
  letter-spacing: 0;
}
/* Last tick (100%): keep its label inside the track. */
.wf-tick:last-child .wf-tick-label {
  left: auto;
  right: 3px;
}

/* --- Rows --- */
.wf-row {
  display: flex;
  align-items: center;
  width: 100%;
  min-height: 28px;
  padding: 0;
  border: none;
  border-bottom: 1px solid var(--lg-border-faint);
  background: transparent;
  color: var(--lg-text);
  font: inherit;
  text-align: left;
  cursor: pointer;
}
.wf-row:hover {
  background: var(--lg-bg-hover);
}
.wf-row.is-open {
  background: var(--lg-bg-active);
}
.wf-row:focus-visible {
  outline: none;
  box-shadow: inset 0 0 0 2px var(--lg-accent);
}

.wf-label {
  flex: 0 0 220px;
  display: flex;
  align-items: baseline;
  gap: var(--lg-space-2);
  min-width: 0;
  padding-right: var(--lg-space-2);
  padding-left: 0;
  margin-left: var(--lg-space-3);
  overflow: hidden;
}
.wf-name {
  font-family: var(--lg-font-mono);
  font-size: var(--lg-fs-xs);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
}
.is-error .wf-name {
  color: var(--lg-danger);
}
.wf-svc {
  flex-shrink: 0;
  color: var(--lg-text-faint);
  font-size: var(--lg-fs-2xs);
  white-space: nowrap;
}

.wf-track {
  position: relative;
  flex: 1;
  min-width: 0;
  height: 28px;
  margin-right: var(--lg-space-3);
}
.wf-bar {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  height: 12px;
  min-width: 2px;
  border-radius: var(--lg-radius-sm);
}
.wf-dur {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  margin-left: var(--lg-space-1);
  color: var(--lg-text-dim);
  font-family: var(--lg-font-mono);
  font-size: var(--lg-fs-2xs);
  white-space: nowrap;
  pointer-events: none;
}
.wf-dur.before {
  margin-left: 0;
  margin-right: var(--lg-space-1);
}

/* --- Expanded span detail --- */
.wf-detail {
  padding: var(--lg-space-2) var(--lg-space-3) var(--lg-space-3);
  border-bottom: 1px solid var(--lg-border-faint);
  background: var(--lg-bg-raised);
  font-family: var(--lg-font-mono);
  font-size: var(--lg-fs-sm);
}
.wf-meta {
  display: flex;
  flex-wrap: wrap;
  gap: var(--lg-space-1) var(--lg-space-4);
  color: var(--lg-text-dim);
}
.meta-k {
  color: var(--lg-text-faint);
  margin-right: var(--lg-space-2);
  text-transform: uppercase;
  font-size: var(--lg-fs-2xs);
  letter-spacing: 0.04em;
}
.status-err {
  color: var(--lg-danger);
  font-weight: 600;
}
.status-ok {
  color: var(--lg-ok);
}
.wf-status-msg {
  margin: var(--lg-space-2) 0 0;
  padding: var(--lg-space-2) var(--lg-space-3);
  border: 1px solid color-mix(in srgb, var(--lg-danger) 30%, transparent);
  border-radius: var(--lg-radius-md);
  background: color-mix(in srgb, var(--lg-danger) 10%, var(--lg-bg));
  color: var(--lg-danger);
  white-space: pre-wrap;
  word-break: break-word;
}
.wf-attrs {
  margin: var(--lg-space-2) 0 0;
  padding: var(--lg-space-2) var(--lg-space-3);
  list-style: none;
  background: var(--lg-bg);
  border: 1px solid var(--lg-border-faint);
  border-radius: var(--lg-radius-md);
}
.wf-attr {
  display: flex;
  align-items: baseline;
  gap: var(--lg-space-1);
  padding: 2px 0;
  min-width: 0;
}
.attr-k {
  color: var(--lg-accent-soft);
  flex-shrink: 0;
}
.attr-eq {
  color: var(--lg-text-faint);
}
.attr-v {
  color: var(--lg-text);
  min-width: 0;
  word-break: break-word;
}
.wf-no-attrs {
  margin: var(--lg-space-2) 0 0;
  color: var(--lg-text-faint);
  font-size: var(--lg-fs-xs);
}

/* Mobile: the timeline scrolls horizontally, but the expanded DETAIL should be
   readable without scrolling — pin it to the scroller's viewport (sticky) and
   size it to the visible width (100vw minus the detail-body padding and the
   scroller border) so nothing clips off-screen. */
@media (max-width: 768px) {
  .wf-detail {
    position: sticky;
    left: 0;
    width: calc(100vw - 2 * var(--lg-space-3) - 2px);
  }
}
</style>
